/**
 * The admin scan-upload queue.
 *
 * Lives outside React (one instance for the whole app, held by
 * UploadQueueProvider) so nothing a page does — a login redirect after the
 * session lapses, navigating to another order — can drop the files it holds.
 *
 * Each file goes through:
 *   pending   selected, waiting for the admin to press Upload
 *   queued    waiting for an upload slot
 *   checking  hashing (CRC32) and registering with Loupe, which either says S3
 *             already has these exact bytes (→ skipped) or signs a PUT
 *   uploading PUT to S3, signed over the CRC32 so corrupt bytes are refused
 *   waiting   backing off after a transient failure, retried automatically
 *   done / skipped / failed
 *
 * Upload URLs are requested per file, just before its PUT starts — never for a
 * whole roll up front, where files queued behind the first few would find their
 * URLs expired by the time a slot came free.
 */
import type { FileDescriptor, PutOutcome, RegisterOutcome } from './transport';

export type UploadJobStatus =
  | 'pending'
  | 'queued'
  | 'checking'
  | 'uploading'
  | 'waiting'
  | 'done'
  | 'skipped'
  | 'failed';

export interface UploadJob {
  id: string;
  orderId: string;
  rollId: string;
  file: File;
  status: UploadJobStatus;
  /** 0–100 while uploading. */
  progress: number;
  /** Transient failures so far; reset by a manual retry. */
  attempts: number;
  /** Why the file is waiting or failed. */
  error: string | null;
}

export interface UploadQueueSnapshot {
  jobs: readonly UploadJob[];
  /** The session lapsed; nothing new starts until sign-in. */
  authPaused: boolean;
}

export interface UploadQueueDeps {
  hash: (file: File) => Promise<string>;
  register: (orderId: string, rollId: string, files: FileDescriptor[]) => Promise<RegisterOutcome>;
  put: (
    url: string,
    headers: Record<string, string>,
    file: File,
    onProgress: (percent: number) => void
  ) => Promise<PutOutcome>;
  /** Called once when a request finds the session gone. */
  onUnauthorized: () => void;
  /** A file is now in S3 (uploaded or already there) — refresh the roll's scans. */
  onFileLanded?: (orderId: string, rollId: string) => void;
  /** Simultaneous PUTs. Browsers allow 6 connections per host; stay under it. */
  concurrency?: number;
  /** Transient failures tolerated per file before it's marked failed. */
  maxAttempts?: number;
  /** Delay before each automatic retry; the last entry repeats. */
  backoffMs?: number[];
  sleep?: (ms: number) => Promise<void>;
  isOnline?: () => boolean;
  waitForOnline?: () => Promise<void>;
}

/** States in which a file still needs the page open. */
export const ACTIVE_STATUSES: ReadonlySet<UploadJobStatus> = new Set([
  'queued',
  'checking',
  'uploading',
  'waiting',
]);

/** A fresh URL is cheap, but an endless expire→re-sign loop is a bug. */
const MAX_URL_REFRESHES = 3;

/**
 * Failed PUTs in a row, across all files and with no success between, after
 * which the problem is taken to be systematic — a misconfiguration, not a bad
 * file or a blip — and files still waiting aren't attempted. Without this, a
 * failure every file hits (a CORS preflight refused, say, which surfaces only
 * as a network error) has every file in the queue run through all its retries
 * at once: over a thousand registrations in three minutes on the first
 * production roll, enough to trip Shopify's rate limit.
 *
 * Refusals S3 explains (403/400 with a code) are unambiguous, so a few will
 * do; bare network errors could be a real blip, so they get more rope.
 */
const REFUSALS_BEFORE_STOPPING = 3;
const FAILURES_BEFORE_STOPPING = 8;

const defaultSleep = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));
const defaultIsOnline = () => (typeof navigator === 'undefined' ? true : navigator.onLine !== false);
const defaultWaitForOnline = () =>
  new Promise<void>((resolve) => window.addEventListener('online', () => resolve(), { once: true }));

export class UploadQueue {
  private snapshot: UploadQueueSnapshot = { jobs: [], authPaused: false };
  private listeners = new Set<() => void>();
  private active = 0;
  private seq = 0;
  private consecutiveRefusals = 0;
  private consecutiveFailures = 0;
  /** Hashes by job id, started ahead of time so a free slot rarely waits on one. */
  private hashes = new Map<string, Promise<string>>();
  /** Hashing is disk- and CPU-bound; one file at a time, in queue order. */
  private hashChain: Promise<unknown> = Promise.resolve();
  private readonly concurrency: number;
  private readonly maxAttempts: number;
  private readonly backoffMs: number[];
  private readonly sleep: (ms: number) => Promise<void>;
  private readonly isOnline: () => boolean;
  private readonly waitForOnline: () => Promise<void>;

  constructor(private readonly deps: UploadQueueDeps) {
    this.concurrency = deps.concurrency ?? 4;
    this.maxAttempts = deps.maxAttempts ?? 5;
    this.backoffMs = deps.backoffMs ?? [2_000, 5_000, 15_000, 30_000];
    this.sleep = deps.sleep ?? defaultSleep;
    this.isOnline = deps.isOnline ?? defaultIsOnline;
    this.waitForOnline = deps.waitForOnline ?? defaultWaitForOnline;
  }

  // --- Store interface (useSyncExternalStore) ---

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): UploadQueueSnapshot => this.snapshot;

  // --- Commands ---

  /** Add selected files to a roll as `pending`. */
  add(orderId: string, rollId: string, files: File[]): void {
    if (files.length === 0) return;
    const added = files.map<UploadJob>((file) => ({
      id: `upload-${++this.seq}`,
      orderId,
      rollId,
      file,
      status: 'pending',
      progress: 0,
      attempts: 0,
      error: null,
    }));
    this.set({ jobs: [...this.snapshot.jobs, ...added] });
  }

  /** Start uploading a roll's pending files. */
  start(orderId: string, rollId: string): void {
    this.requeue((j) => j.orderId === orderId && j.rollId === rollId && j.status === 'pending');
  }

  /** Give a roll's failed files a fresh set of attempts. */
  retryFailed(orderId: string, rollId: string): void {
    this.requeue((j) => j.orderId === orderId && j.rollId === rollId && j.status === 'failed');
  }

  /** Remove a file that hasn't started (pending) or has given up (failed). */
  remove(jobId: string): void {
    const job = this.get(jobId);
    if (!job || (job.status !== 'pending' && job.status !== 'failed')) return;
    this.hashes.delete(jobId);
    this.set({ jobs: this.snapshot.jobs.filter((j) => j.id !== jobId) });
  }

  /** Signed back in: carry on where the queue stopped. */
  resume(): void {
    if (!this.snapshot.authPaused) return;
    this.set({ authPaused: false });
    this.pump();
  }

  // --- Internals ---

  private set(patch: Partial<UploadQueueSnapshot>): void {
    this.snapshot = { ...this.snapshot, ...patch };
    for (const listener of this.listeners) listener();
  }

  private get(id: string): UploadJob | undefined {
    return this.snapshot.jobs.find((j) => j.id === id);
  }

  private update(id: string, patch: Partial<UploadJob>): void {
    this.set({ jobs: this.snapshot.jobs.map((j) => (j.id === id ? { ...j, ...patch } : j)) });
  }

  private requeue(match: (job: UploadJob) => boolean): void {
    const ids = this.snapshot.jobs.filter(match).map((j) => j.id);
    if (ids.length === 0) return;
    const idSet = new Set(ids);
    // Starting or retrying is the admin saying "try again": give the breaker a
    // clean slate rather than letting one more failure trip it straight away.
    this.consecutiveRefusals = 0;
    this.consecutiveFailures = 0;
    this.set({
      jobs: this.snapshot.jobs.map((j) =>
        idSet.has(j.id) ? { ...j, status: 'queued', progress: 0, attempts: 0, error: null } : j
      ),
    });
    for (const id of ids) this.prefetchHash(id);
    this.pump();
  }

  private prefetchHash(id: string): Promise<string> {
    const existing = this.hashes.get(id);
    if (existing) return existing;
    const job = this.get(id);
    if (!job) return Promise.reject(new Error('Upload removed'));
    const hash = this.hashChain.then(() => this.deps.hash(job.file));
    this.hashChain = hash.catch(() => undefined);
    this.hashes.set(id, hash);
    return hash;
  }

  private pump(): void {
    while (!this.snapshot.authPaused && this.active < this.concurrency) {
      const next = this.snapshot.jobs.find((j) => j.status === 'queued');
      if (!next) return;
      this.active++;
      this.update(next.id, { status: 'checking', progress: 0 });
      void this.run(next.id).finally(() => {
        this.active--;
        this.pump();
      });
    }
  }

  private async run(id: string): Promise<void> {
    let urlRefreshes = 0;
    let rehashed = false;

    for (;;) {
      const job = this.get(id);
      if (!job) return;

      let crc32: string;
      try {
        crc32 = await this.prefetchHash(id);
      } catch {
        this.hashes.delete(id);
        return this.fail(id, 'Could not read this file from disk');
      }

      const registered = await this.deps.register(job.orderId, job.rollId, [
        { filename: job.file.name, size: job.file.size, crc32 },
      ]);
      if (registered.kind === 'unauthorized') {
        // No attempt spent: the file goes back in line for after sign-in.
        this.update(id, { status: 'queued' });
        return this.pauseForAuth();
      }
      if (registered.kind === 'rejected') return this.fail(id, registered.message);
      if (registered.kind === 'transient') return this.retryLater(id, registered.message);

      const entry = registered.files.find((f) => f.filename === job.file.name);
      if (!entry) return this.retryLater(id, 'Unexpected response from Loupe');
      if (entry.status === 'uploaded') return this.finish(id, 'skipped');
      if (!entry.upload_url) return this.retryLater(id, 'Unexpected response from Loupe');

      this.update(id, { status: 'uploading', progress: 0, error: null });
      const put = await this.deps.put(
        entry.upload_url,
        entry.upload_headers ?? { 'Content-Type': 'image/tiff' },
        job.file,
        (percent) => {
          if (this.get(id)?.progress !== percent) this.update(id, { progress: percent });
        }
      );

      switch (put.kind) {
        case 'ok':
          return this.finish(id, 'done');
        case 'expired':
          if (++urlRefreshes > MAX_URL_REFRESHES) return this.retryLater(id, 'Upload link expired');
          this.update(id, { status: 'checking', progress: 0 });
          continue;
        case 'checksum':
          // The bytes S3 received don't hash to what was read earlier: the file
          // changed on disk, or a read went wrong. Hash it again, once.
          if (rehashed) {
            return this.fail(
              id,
              "The file's contents didn't match its checksum. It may have changed on disk — re-add it and try again."
            );
          }
          rehashed = true;
          this.hashes.delete(id);
          this.update(id, { status: 'checking', progress: 0 });
          continue;
        case 'refused':
          this.consecutiveFailures++;
          if (++this.consecutiveRefusals >= REFUSALS_BEFORE_STOPPING) {
            this.fail(id, put.message);
            return this.stopWaitingFiles(`S3 refused the last ${this.consecutiveRefusals} uploads (${put.message})`);
          }
          return this.fail(id, put.message);
        case 'transient':
          // Being offline is not the upload's fault, and retryLater waits for
          // the connection without charging it; only count real failures.
          if (this.isOnline() && ++this.consecutiveFailures >= FAILURES_BEFORE_STOPPING) {
            this.fail(id, put.message);
            return this.stopWaitingFiles(
              `the last ${this.consecutiveFailures} uploads all failed (${put.message})`
            );
          }
          return this.retryLater(id, put.message);
      }
    }
  }

  /** Fail every file still waiting for a slot or a retry, saying why. */
  private stopWaitingFiles(reason: string): void {
    const notAttempted = `Not attempted — ${reason}. Retry once that's fixed.`;
    this.set({
      jobs: this.snapshot.jobs.map((j) =>
        j.status === 'queued' || j.status === 'waiting'
          ? { ...j, status: 'failed', progress: 0, error: notAttempted }
          : j
      ),
    });
  }

  private finish(id: string, status: 'done' | 'skipped'): void {
    this.consecutiveRefusals = 0;
    this.consecutiveFailures = 0;
    const job = this.get(id);
    this.hashes.delete(id);
    this.update(id, { status, progress: 100, error: null });
    if (job) this.deps.onFileLanded?.(job.orderId, job.rollId);
  }

  private fail(id: string, message: string): void {
    this.update(id, { status: 'failed', progress: 0, error: message });
  }

  private pauseForAuth(): void {
    if (this.snapshot.authPaused) return;
    this.set({ authPaused: true });
    this.deps.onUnauthorized();
  }

  /**
   * Back off and try again without holding an upload slot. Being offline
   * doesn't count against the file — it waits for the connection instead.
   */
  private retryLater(id: string, message: string): void {
    const job = this.get(id);
    if (!job) return;

    if (!this.isOnline()) {
      this.update(id, { status: 'waiting', progress: 0, error: 'Offline — will resume when the connection returns' });
      void this.waitForOnline().then(() => this.wake(id));
      return;
    }

    const attempts = job.attempts + 1;
    if (attempts >= this.maxAttempts) {
      return this.fail(id, `${message} — gave up after ${attempts} attempts`);
    }
    const delay = this.backoffMs[Math.min(attempts - 1, this.backoffMs.length - 1)];
    this.update(id, { status: 'waiting', progress: 0, attempts, error: `${message} — retrying` });
    void this.sleep(delay).then(() => this.wake(id));
  }

  private wake(id: string): void {
    if (this.get(id)?.status !== 'waiting') return;
    this.update(id, { status: 'queued' });
    this.pump();
  }
}
